import type { JSX } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, Empty, Skeleton } from 'antd';
import {
  ArrowLeftOutlined,
  CheckOutlined,
  EditOutlined,
  FileTextOutlined,
  LockOutlined,
  ReadOutlined,
  RightOutlined,
  SafetyCertificateOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';

import { authApi } from '../lib/authApi';
import { useRoleLabels } from '../lib/useRoleLabels';
import { educationApi, type MyPathDto } from '../lib/wikiApi';
import { Ring } from '../components/ds';
import { pathDays } from '../components/learn/learn-utils';
import './Learn.css';

/**
 * Экран траектории (HUB 2.0, PageLearn «Траектория»): шапка с кольцом и
 * полоской дней «день X из N», слева — шаги лентой с номерами, сроками,
 * статусом и «Продолжить» у текущего шага, справа — прогресс, настройки
 * пути и другие траектории сотрудника. Данные те же, что в блоке
 * «Моя траектория» на /education (`GET /wiki/education/paths/mine`), просто
 * развёрнуто на отдельной странице, чтобы путь можно было открыть и изучить.
 */
const KIND_LABEL: Record<string, string> = {
  course: 'Курс',
  standard: 'Стандарт · ознакомиться',
  checklist: 'Чек-лист · практика с наставником',
  assessment: 'Аттестация',
};

function kindIcon(kind: string): JSX.Element {
  if (kind === 'course') return <ReadOutlined />;
  if (kind === 'standard') return <FileTextOutlined />;
  if (kind === 'assessment') return <SafetyCertificateOutlined />;
  return <CheckOutlined />;
}

function fmtDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' }) : '—';
}

/** Сколько дней прошло с назначения — «День 4 из 30» в правой колонке. */
function daysSince(iso: string): number {
  return Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000) + 1);
}

function daysLeft(iso: string | null): number | null {
  return iso ? Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000) : null;
}

export function PathPage(): JSX.Element {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const navigate = useNavigate();
  const me = useQuery({ queryKey: ['wiki', 'me'], queryFn: () => authApi.me(), retry: false });
  const q = useQuery({ queryKey: ['wiki', 'education', 'paths'], queryFn: () => educationApi.myPaths(), retry: false });
  const { text: rolesText } = useRoleLabels();

  if (q.isLoading) return <Skeleton active paragraph={{ rows: 8 }} />;
  const all = q.data ?? [];
  const p = all.find((x) => x.assignmentId === assignmentId) ?? null;
  if (!p) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Link to="/education" className="fk-back"><ArrowLeftOutlined /> Обучение</Link>
        <Alert type="info" showIcon message="Траектория не найдена" description="Возможно, назначение сняли или путь выключили." />
      </div>
    );
  }

  const others = all.filter((x) => x.assignmentId !== p.assignmentId);
  const pct = p.totalSteps === 0 ? 0 : Math.round((p.doneSteps / p.totalSteps) * 100);
  const days = pathDays(p.assignedAt, p.dueAt, p.steps.map((s) => s.dueAt));
  const openStep = (s: MyPathDto['steps'][number]): void => {
    if (s.locked) return;
    if (s.courseId) navigate(`/course/${s.courseId}`);
    else if (s.articleSlug) navigate(`/article/${encodeURIComponent(s.articleSlug)}`);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Link to="/education" className="fk-back"><ArrowLeftOutlined /> Обучение</Link>

      <div className="fk-path-grid">
        {/* ─── Шаги ─── */}
        <div className="fk-card fk-card-pad">
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 }}>
            <span className="fk-path-badge">
              Траектория роли{p.forRoles.length > 0 ? ` · ${rolesText(p.forRoles)}` : ''}
            </span>
            <span style={{ fontSize: 12.5, color: 'var(--fk-text-muted)' }}>
              {p.autoAssign ? 'Назначена автоматически при найме' : 'Назначена'} {fmtDate(p.assignedAt)}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 18, alignItems: 'center', margin: '4px 0 6px' }}>
            <Ring value={pct} size={72} stroke={7} doneColor="var(--fk-success)" />
            <div style={{ minWidth: 0, flex: 1 }}>
              <h1 style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-.5px', margin: 0, color: 'var(--fk-text)' }}>{p.name}</h1>
              <div style={{ fontSize: 13, color: 'var(--fk-text-2)', marginTop: 2 }}>
                {p.completedAt ? 'Траектория пройдена' : `День ${days.day} из ${days.total}`} · {p.doneSteps} из {p.totalSteps} шагов{p.dueAt ? ` · срок до ${fmtDate(p.dueAt)}` : ''}
                {days.overdue && !p.completedAt ? <b style={{ color: 'var(--fk-error)' }}> · срок прошёл</b> : null}
              </div>
              <div className="fk-learn-days" aria-label={`День ${days.day} из ${days.total}`}>{days.cells.map((d, i) => <span key={i} data-d={p.completedAt ? 'past' : d} />)}</div>
            </div>
          </div>
          {p.description && <p style={{ margin: '8px 0 4px', fontSize: 14, color: 'var(--fk-text-2)', lineHeight: 1.55 }}>{p.description}</p>}
          <p style={{ margin: 0, fontSize: 13, color: 'var(--fk-text-muted)', lineHeight: 1.55 }}>
            {p.totalSteps} шагов: курсы, стандарты, практика с наставником и аттестация. Каждый следующий шаг
            открывается после предыдущего. Дедлайны считаются от даты назначения.
          </p>

          <div className="fk-path-steps">
            {p.steps.map((s, i) => {
              const left = daysLeft(s.dueAt);
              const overdue = !s.done && left != null && left < 0;
              const status = s.done ? 'Пройдено' : s.locked ? 'Откроется позже' : overdue ? `Просрочено ${-(left ?? 0)} дн.` : 'Доступно';
              return (
                <div className="fk-path-step" key={s.id}>
                  <div className="fk-pstep-rail">
                    <span className="fk-pstep-dot" data-done={s.done} data-locked={s.locked}>
                      {s.done ? <CheckOutlined /> : s.locked ? <LockOutlined /> : i + 1}
                    </span>
                    {i < p.steps.length - 1 && <span className="fk-pstep-line" data-done={s.done} />}
                  </div>
                  <button type="button" className="fk-pstep-card" disabled={s.locked} onClick={() => openStep(s)}>
                    <span className="fk-pstep-icon">{kindIcon(s.kind)}</span>
                    <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                      <span className="fk-pstep-kind">{KIND_LABEL[s.kind] ?? s.kind}</span>
                      <span className="fk-pstep-title">{s.title}</span>
                      {s.progressPercent != null && !s.done && s.progressPercent > 0 && (
                        <span className="fk-pstep-sub">Пройдено {s.progressPercent}%</span>
                      )}
                    </span>
                    <span style={{ textAlign: 'right', flexShrink: 0 }}>
                      <span className="fk-pstep-due">{s.dueAt ? `до ${fmtDate(s.dueAt)}` : 'без срока'}</span>
                      <span className="fk-pstep-status" data-tone={s.done ? 'ok' : overdue ? 'bad' : s.locked ? 'mute' : 'go'}>{status}</span>
                    </span>
                    {s.id === p.nextStepId && !s.done && !s.locked ? (
                      // Карточка шага — сама кнопка; внутри только вид кнопки (вложенный <button> недопустим).
                      <span className="fk-ds-btn fk-ds-btn--primary fk-ds-btn--sm" aria-hidden>Продолжить</span>
                    ) : !s.locked ? <RightOutlined style={{ fontSize: 11, color: 'var(--fk-text-muted)', flexShrink: 0 }} /> : null}
                  </button>
                </div>
              );
            })}
            {p.steps.length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="В траектории пока нет шагов" />}
          </div>
        </div>

        {/* ─── Правая колонка ─── */}
        <aside style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="fk-card fk-card-pad">
            <div className="fk-rail-title">Прогресс</div>
            <div style={{ fontSize: 30, fontWeight: 800, color: 'var(--fk-indigo)', lineHeight: 1.1 }}>
              {p.doneSteps} / {p.totalSteps}
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--fk-text-muted)', marginTop: 2 }}>
              День {daysSince(p.assignedAt)}
              {p.dueAt ? ` · дедлайн ${fmtDate(p.dueAt)}` : ''}
            </div>
            <div className="fk-bar"><span style={{ width: `${pct}%` }} /></div>
            {p.completedAt && (
              <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, color: 'var(--fk-success)' }}>
                <TrophyOutlined /> Траектория пройдена
              </div>
            )}
          </div>

          <div className="fk-card fk-card-pad">
            <div className="fk-rail-title">Настройки траектории</div>
            <div className="fk-rail-row"><span>Для ролей</span><b>{rolesText(p.forRoles, 'все')}</b></div>
            <div className="fk-rail-row"><span>Автоназначение при найме</span><b>{p.autoAssign ? 'вкл' : 'выкл'}</b></div>
            <div className="fk-rail-row"><span>Назначена</span><b>{fmtDate(p.assignedAt)}</b></div>
            <div className="fk-rail-row"><span>Дедлайн</span><b>{fmtDate(p.dueAt)}</b></div>
            <div className="fk-rail-row"><span>Напоминания</span><b>−3 дн · в день · после</b></div>
            <div className="fk-rail-row"><span>Канал</span><b>Telegram / MAX · колокол</b></div>
            {me.data?.canAdmin && (
              <Link to="/admin/paths" className="fk-btn fk-btn-ghost fk-btn-sm" style={{ marginTop: 10, justifyContent: 'center' }}>
                <EditOutlined /> Редактировать траекторию
              </Link>
            )}
          </div>

          {others.length > 0 && (
            <div className="fk-card fk-card-pad">
              <div className="fk-rail-title">Другие траектории</div>
              {others.map((o) => (
                <Link key={o.assignmentId} to={`/path/${o.assignmentId}`} className="fk-rail-link">
                  {o.name}
                  <span>{o.doneSteps}/{o.totalSteps}</span>
                </Link>
              ))}
            </div>
          )}
        </aside>
      </div>

      <style>{`
        .fk-back{display:inline-flex;align-items:center;gap:8px;font-size:13px;font-weight:600;color:var(--fk-text-2)}
        .fk-path-grid{display:grid;grid-template-columns:minmax(0,1fr) 300px;gap:16px;align-items:start}
        .fk-path-badge{display:inline-flex;padding:3px 8px;border-radius:4px;background:var(--fk-orange-soft);color:var(--fk-orange);font-size:11.5px;font-weight:800;text-transform:uppercase;letter-spacing:.05em}
        .fk-path-steps{display:flex;flex-direction:column;margin-top:18px}
        .fk-path-step{display:flex;gap:14px}
        .fk-pstep-rail{display:flex;flex-direction:column;align-items:center;width:32px;flex-shrink:0}
        .fk-pstep-dot{width:32px;height:32px;border-radius:50%;display:grid;place-items:center;flex-shrink:0;font-size:13px;font-weight:800;background:var(--fk-surface);color:var(--fk-orange);border:2px solid var(--fk-orange)}
        .fk-pstep-dot[data-done="true"]{background:var(--fk-success);border-color:var(--fk-success);color:#fff}
        .fk-pstep-dot[data-locked="true"]{background:var(--fk-bg-subtle);border-color:var(--fk-border-strong);color:var(--fk-text-muted)}
        .fk-pstep-line{flex:1;width:2px;min-height:14px;background:var(--fk-border)}
        .fk-pstep-line[data-done="true"]{background:var(--fk-success)}
        .fk-pstep-card{display:flex;align-items:center;gap:12px;flex:1;min-width:0;margin-bottom:10px;padding:12px 14px;border-radius:8px;border:1px solid var(--fk-border);background:var(--fk-surface);cursor:pointer;font-family:inherit;min-height:64px;transition:border-color .1s,box-shadow .1s}
        .fk-pstep-card:hover:not(:disabled){border-color:var(--fk-orange);box-shadow:var(--fk-shadow-card)}
        .fk-pstep-card:disabled{cursor:not-allowed;opacity:.55}
        .fk-pstep-icon{display:grid;place-items:center;width:40px;height:40px;border-radius:6px;background:var(--fk-orange-soft);color:var(--fk-orange);font-size:17px;flex-shrink:0}
        .fk-pstep-kind{display:block;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:var(--fk-text-muted)}
        .fk-pstep-title{display:block;font-size:15px;font-weight:700;color:var(--fk-text);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
        .fk-pstep-sub{display:block;font-size:12px;color:var(--fk-text-muted)}
        .fk-pstep-due{display:block;font-size:12px;color:var(--fk-text-muted)}
        .fk-pstep-status{display:block;font-size:12px;font-weight:700}
        .fk-pstep-status[data-tone="ok"]{color:var(--fk-success)}
        .fk-pstep-status[data-tone="bad"]{color:var(--fk-error)}
        .fk-pstep-status[data-tone="go"]{color:var(--fk-orange)}
        .fk-pstep-status[data-tone="mute"]{color:var(--fk-text-muted)}
        .fk-rail-title{font-size:11.5px;font-weight:800;text-transform:uppercase;letter-spacing:.06em;color:var(--fk-text-muted);margin-bottom:8px}
        .fk-rail-row{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:5px 0;font-size:12.5px;color:var(--fk-text-2);border-bottom:1px dashed var(--fk-border)}
        .fk-rail-row:last-of-type{border-bottom:none}
        .fk-rail-row b{color:var(--fk-text);text-align:right}
        .fk-rail-link{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:7px 0;font-size:13px;font-weight:600;color:var(--fk-text)}
        .fk-rail-link:hover{color:var(--fk-orange)}
        .fk-rail-link span{font-size:12px;font-weight:700;color:var(--fk-text-muted)}
        .fk-bar{height:8px;border-radius:4px;background:var(--fk-border);overflow:hidden;margin-top:10px}
        .fk-bar>span{display:block;height:100%;border-radius:4px;background:var(--fk-orange)}
        @media (max-width: 980px){ .fk-path-grid{grid-template-columns:1fr} }
        @media (max-width: 640px){ .fk-pstep-card{flex-wrap:wrap} .fk-pstep-card .fk-ds-btn{width:100%;justify-content:center;min-height:44px} .fk-pstep-title{white-space:normal} }
      `}</style>
    </div>
  );
}
